export type ChallengeSubmissionMode = 'photo_and_text' | 'photo_only' | 'text_only';

export type ChallengeMission = {
  id: string;
  title: string;
  body: string;
  photoHint: string;
  submissionMode: ChallengeSubmissionMode;
  textLabel: string;
  textPlaceholder: string;
};

/** 학생 참여 화면에 보이는 문구(편집 가능) */
export type ChallengeCopy = {
  kicker: string;
  description: string;
  startButton: string;
  guideText: string;
  submitButton: string;
  successTitle: string;
  successMessage: string;
  topLogoText: string;
  bottomLogoText: string;
};

export type ChallengeContent = ChallengeCopy & {
  missions: ChallengeMission[];
};

export type ChallengeItem = {
  /** 과제노트 프로젝트 과제의 하위 과제 id 와 동일 */
  id: string;
  projectId: string;
  sortOrder: number;
  isVisible: boolean;
  title: string;
  content: ChallengeContent;
  thumbnailUrl: string | null;
  thumbnailPath: string | null;
  revision: number;
  updatedAt: string;
};

export type ChallengeProject = {
  id: string;
  workspaceId: string;
  gradeId: string;
  assignmentItemId: string;
  ownerUserId: string;
  title: string;
  shareToken: string;
  shareEnabled: boolean;
  /** 챌린지 화면 테마색(과제 색상과 동기화, 빈 값이면 기본 주황) */
  themeColor: string;
  /** 참여 링크 목록 화면(인증과제 카드 위) 문구. 저장 전이면 null */
  intro: Partial<ChallengeProjectIntro> | null;
  updatedAt: string;
};

/** 참여 링크 목록 화면(인증과제 카드 위)에 보이는 프로젝트 설명 */
export type ChallengeProjectIntro = {
  /** 맨 위 작은 영문 문구 */
  eyebrow: string;
  /** 큰 제목 위 한 줄 */
  subtitle: string;
  /** 큰 제목(비우면 프로젝트 이름) */
  headline: string;
  description: string;
  topLogoText: string;
  bottomLogoText: string;
  thumbnailUrl: string | null;
  thumbnailPath: string | null;
};

export type ChallengeAnswer = {
  missionId: string;
  text: string;
  photoPath: string | null;
};

export type ChallengeSubmission = {
  id: string;
  projectId: string;
  itemId: string;
  memberId: string;
  memberNameSnapshot: string;
  answers: ChallengeAnswer[];
  itemSnapshot: { title: string; missions: ChallengeMission[] };
  submittedAt: string;
  updatedAt: string;
};

export const CHALLENGE_SUBMISSION_MODE_LABEL: Record<ChallengeSubmissionMode, string> = {
  photo_and_text: '사진과 글',
  photo_only: '사진만',
  text_only: '글만',
};

export const CHALLENGE_TEXT_MAX = 2000;
export const CHALLENGE_MISSION_MAX = 10;
export const CHALLENGE_THUMB_ASPECT = 1;

export const DEFAULT_CHALLENGE_COPY: ChallengeCopy = {
  kicker: '오늘의 챌린지',
  description: '챌린지를 읽고 인증 항목을 차례대로 완성해 보세요.',
  startButton: '인증 시작하기',
  guideText: '사진과 글로 챌린지를 인증해 주세요. 제출 후에도 선생님 확인 전까지 수정할 수 있어요.',
  submitButton: '제출하기',
  successTitle: '멋져요!',
  successMessage: '챌린지 인증이 정상적으로 제출되었어요.',
  topLogoText: '',
  bottomLogoText: '',
};

export const DEFAULT_CHALLENGE_INTRO: ChallengeProjectIntro = {
  eyebrow: 'CHALLENGE PROJECT',
  subtitle: '',
  headline: '',
  description: '인증과제를 하나씩 눌러 도전하고 제출해 보세요.',
  topLogoText: '',
  bottomLogoText: '',
  thumbnailUrl: null,
  thumbnailPath: null,
};

type IntroTextKey = 'eyebrow' | 'subtitle' | 'headline' | 'description' | 'topLogoText' | 'bottomLogoText';

/** 저장된 값이 없는 칸은 첫 인증과제 로고 문구(예전 방식) → 기본값 순으로 채운다 */
export function normalizeChallengeIntro(
  raw: unknown,
  fallback?: Partial<Pick<ChallengeCopy, 'topLogoText' | 'bottomLogoText'>> | null
): ChallengeProjectIntro {
  const o = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {};
  const pick = (k: IntroTextKey): string => {
    if (typeof o[k] === 'string') return o[k] as string;
    if ((k === 'topLogoText' || k === 'bottomLogoText') && typeof fallback?.[k] === 'string') {
      return fallback[k] as string;
    }
    return DEFAULT_CHALLENGE_INTRO[k];
  };
  const nullable = (k: 'thumbnailUrl' | 'thumbnailPath'): string | null =>
    typeof o[k] === 'string' && o[k] ? (o[k] as string) : null;
  return {
    eyebrow: pick('eyebrow'),
    subtitle: pick('subtitle'),
    headline: pick('headline'),
    description: pick('description'),
    topLogoText: pick('topLogoText'),
    bottomLogoText: pick('bottomLogoText'),
    thumbnailUrl: nullable('thumbnailUrl'),
    thumbnailPath: nullable('thumbnailPath'),
  };
}

export function newChallengeMissionId(): string {
  return `ms-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

export function emptyChallengeMission(): ChallengeMission {
  return {
    id: newChallengeMissionId(),
    title: '인증 항목',
    body: '',
    photoHint: '',
    submissionMode: 'photo_and_text',
    textLabel: '',
    textPlaceholder: '',
  };
}

export function defaultChallengeContent(): ChallengeContent {
  return { ...DEFAULT_CHALLENGE_COPY, missions: [emptyChallengeMission()] };
}

function str(v: unknown, fallback = ''): string {
  return typeof v === 'string' ? v : fallback;
}

function normalizeMode(v: unknown): ChallengeSubmissionMode {
  return v === 'photo_only' || v === 'text_only' ? v : 'photo_and_text';
}

export function normalizeChallengeMission(raw: unknown, index: number): ChallengeMission | null {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  const id = str(o.id).trim() || `ms-legacy-${index}`;
  return {
    id,
    title: str(o.title),
    body: str(o.body),
    photoHint: str(o.photoHint),
    submissionMode: normalizeMode(o.submissionMode),
    textLabel: str(o.textLabel),
    textPlaceholder: str(o.textPlaceholder),
  };
}

export function normalizeChallengeContent(raw: unknown): ChallengeContent {
  const o = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {};
  const missions = Array.isArray(o.missions)
    ? o.missions
        .map((m, i) => normalizeChallengeMission(m, i))
        .filter((m): m is ChallengeMission => m != null)
        .slice(0, CHALLENGE_MISSION_MAX)
    : [];
  const copy = {} as ChallengeCopy;
  (Object.keys(DEFAULT_CHALLENGE_COPY) as Array<keyof ChallengeCopy>).forEach((k) => {
    copy[k] = typeof o[k] === 'string' ? (o[k] as string) : DEFAULT_CHALLENGE_COPY[k];
  });
  return { ...copy, missions };
}

export function missionNeedsPhoto(mode: ChallengeSubmissionMode): boolean {
  return mode !== 'text_only';
}

export function missionNeedsText(mode: ChallengeSubmissionMode): boolean {
  return mode !== 'photo_only';
}

export const CHALLENGE_DEFAULT_THEME = '#f97316';

export function normalizeChallengeThemeColor(color: string | null | undefined): string {
  const c = (color ?? '').trim();
  return /^#[0-9a-f]{6}$/i.test(c) || /^#[0-9a-f]{3}$/i.test(c) ? c.toLowerCase() : '';
}

/** 챌린지 화면 루트에 거는 테마 CSS 변수(App.css 의 --chl-accent* 계열) */
export function challengeThemeStyle(color: string | null | undefined): Record<string, string> | undefined {
  const c = normalizeChallengeThemeColor(color);
  if (!c) return undefined;
  return {
    '--chl-accent': c,
    '--chl-accent-strong': `color-mix(in srgb, ${c} 86%, #000)`,
    '--chl-accent-deep': `color-mix(in srgb, ${c} 68%, #000)`,
    '--chl-accent-light': `color-mix(in srgb, ${c} 78%, #fff)`,
    '--chl-accent-soft': `color-mix(in srgb, ${c} 52%, #fff)`,
    '--chl-accent-pale': `color-mix(in srgb, ${c} 32%, #fff)`,
    '--chl-accent-tint': `color-mix(in srgb, ${c} 9%, #fff)`,
  };
}

/** 인증 사진 썸네일 경로: `<uuid>.webp` → `<uuid>.t.webp` (원본 옆에 저장) */
export function challengePhotoThumbPath(path: string): string {
  return path.replace(/\.(webp|jpg)$/, '.t.$1');
}
